/** Normalize relative / media paths against the API host so <img> can load them. */
export const toAbsoluteMediaUrl = (value) => {
  if (value == null) return ''
  if (typeof value !== 'string') return ''
  const url = value.trim()
  if (!url || url === 'null' || url === 'undefined') return ''
  if (/^(https?:|data:|blob:)/i.test(url)) return url
  if (url.startsWith('//')) return `https:${url}`

  const apiBase = String(process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '')
  if (!apiBase) return url

  // Media files are usually hosted on the same origin as the API (strip /api suffix if present)
  const origin = apiBase.replace(/\/api(?:\/v\d+)?$/i, '')
  if (url.startsWith('/')) return `${origin}${url}`
  return `${origin}/${url}`
}

const pickImageString = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return toAbsoluteMediaUrl(value)
  if (typeof value === 'object') {
    return toAbsoluteMediaUrl(
      value.image
      || value.url
      || value.src
      || value.profile_pic
      || value.file
      || value.path
      || ''
    )
  }
  return ''
}

/**
 * Resolve a displayable profile photo URL from user / user-account API shapes.
 * Prefer uploaded photo, then nested avatar image, then other common fields.
 */
export const resolveProfileImage = (account) => {
  if (!account || typeof account !== 'object') return ''

  // Sometimes the account is nested under `user`
  const source = account.user && typeof account.user === 'object' && !account.profile_pic && !account.avatar
    ? account.user
    : account

  const candidates = [
    source.profile_pic,
    source.profile_pic_url,
    source.profile_image,
    source.profile_photo,
    source.photo,
    source.photo_url,
    source.picture,
    source.avatar_image,
    source.image,
    source.avatar, // may be object { image } or absolute URL string
    source.profileImageUrl, // client-side cache field
  ]

  for (const candidate of candidates) {
    const resolved = pickImageString(candidate)
    if (resolved) return resolved
  }

  return ''
}

export const resolveAvatarId = (account) => {
  if (!account || typeof account !== 'object') return ''
  const source = account.user && typeof account.user === 'object' ? account.user : account
  const avatar = source.avatar
  if (avatar != null && typeof avatar === 'object') {
    return avatar.id ?? source.avatar_id ?? ''
  }
  return source.avatar_id ?? avatar ?? ''
}

/** Patch for Redux: always keep a non-empty display URL when we know one. */
export const withProfileImage = (account = {}, fallbackUrl = '') => {
  const imageUrl = resolveProfileImage(account) || toAbsoluteMediaUrl(fallbackUrl) || ''
  if (!account || typeof account !== 'object') {
    return imageUrl ? { profile_pic: imageUrl, profileImageUrl: imageUrl } : {}
  }
  return {
    ...account,
    profile_pic: imageUrl || account.profile_pic || '',
    profileImageUrl: imageUrl,
  }
}
